import {
  DEV_SERVER_GROUP_LABELS,
  DEV_SERVER_GROUP_ORDER,
  DEV_SERVER_IDS,
  type DevServerGroup,
  type DevServerId,
  type DevServerStatus,
  type DevServersSnapshot,
} from '../../lib/devServersRegistry';
import { useDevServers } from '../../hooks/useDevServers';
import { DevServerControl } from './DevServerControl';

function serversInGroup(group: DevServerGroup, snapshot: DevServersSnapshot) {
  return DEV_SERVER_IDS.filter((id) => snapshot.servers[id]?.group === group).sort(
    (a, b) => (snapshot.servers[a]?.port ?? 0) - (snapshot.servers[b]?.port ?? 0),
  );
}

function ServerRow({ id, server }: { id: DevServerId; server: DevServerStatus }) {
  const missing = !server.cwdExists;
  const title = [
    server.label,
    server.cwd,
    server.lastError,
    server.conflictNote,
    server.statusOnly ? '(status only)' : '',
    server.archived ? '(archived)' : '',
  ]
    .filter(Boolean)
    .join(' — ');

  return (
    <div
      className="flex items-center gap-1.5 min-w-0 text-sm text-charcoal-800"
      title={title}
    >
      <DevServerControl serverId={id} />
      <span className={`truncate font-medium ${server.archived ? 'text-charcoal-500' : ''}`}>
        {server.label}
      </span>
      <span className="text-[10px] font-mono text-charcoal-400 shrink-0">:{server.port}</span>
      {server.conflictNote && (
        <span className="text-[10px] text-[var(--mustard)] shrink-0" title={server.conflictNote}>
          ⚠
        </span>
      )}
      {missing && (
        <span className="text-[10px] text-[var(--mustard)] shrink-0" title="Folder missing">
          ∅
        </span>
      )}
    </div>
  );
}

function GroupBlock({
  group,
  snapshot,
  compact,
  defaultOpen,
}: {
  group: DevServerGroup;
  snapshot: DevServersSnapshot;
  compact?: boolean;
  defaultOpen?: boolean;
}) {
  const ids = serversInGroup(group, snapshot);
  if (ids.length === 0) return null;

  const running = ids.filter((id) => snapshot.servers[id]?.running).length;
  const label = `${DEV_SERVER_GROUP_LABELS[group]} (${running}/${ids.length})`;

  const grid = (
    <div
      className={
        compact
          ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-3 gap-y-1.5'
          : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-3 gap-y-1.5'
      }
    >
      {ids.map((id) => {
        const server = snapshot.servers[id];
        if (!server) return null;
        return <ServerRow key={id} id={id} server={server} />;
      })}
    </div>
  );

  if (group === 'archived' || group === 'platform') {
    return (
      <details className="group/arch" open={defaultOpen}>
        <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-wide text-charcoal-600 mb-1.5 list-none flex items-center gap-1">
          <span className="text-charcoal-400 group-open/arch:rotate-90 transition-transform">▸</span>
          {label}
        </summary>
        <div className="mb-2 pl-3 border-l border-charcoal-100">{grid}</div>
      </details>
    );
  }

  return (
    <div className="mb-2 last:mb-0">
      <h3 className="text-[11px] font-bold uppercase tracking-wide text-charcoal-600 mb-1.5">
        {label}
      </h3>
      {grid}
    </div>
  );
}

/** Grouped grid of verified local dev servers — status dot, start, port. */
export function DevServersSummaryPanel({
  className = 'mb-6',
  compact = false,
}: {
  className?: string;
  compact?: boolean;
}) {
  const { snapshot } = useDevServers();

  if (!import.meta.env.DEV || !snapshot.available) return null;

  return (
    <div className={` border border-charcoal-200 bg-[var(--bg-raised)] shadow-sm p-3 ${className}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <h2 className="text-xs font-bold uppercase tracking-wide text-charcoal-700">
          Local dev servers
        </h2>
        <span className="text-[10px] text-charcoal-400">
          ● green = server up · red = down · hover card dot for port
        </span>
      </div>
      {DEV_SERVER_GROUP_ORDER.map((group) => (
        <GroupBlock
          key={group}
          group={group}
          snapshot={snapshot}
          compact={compact}
          defaultOpen={!compact && group === 'platform'}
        />
      ))}
    </div>
  );
}
